import { useState } from 'react';

import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileNav } from '../../../../components/profile/ProfileNav';
import { formatDateOnly, formatDateTime, toIso } from '../../../../lib/formatDate';
import type { PraiseSurface } from '../praiseSurface';
import { AWARD_STAGE, type AwardDetail, type AwardStageGate } from '../praiseTypes';
import { NomineesPanel } from './NomineesPanel';
import { DeliberationModal, FinalAwardingModal } from './StageDecisionModals';
import { useStageDocumentsRow } from './StageDocuments';

/** The rail's entries, in the order a nominee passes them. */
const GATES: { key: AwardStageGate; label: string }[] = [
  { key: 'qualification', label: 'Requirements Qualification' },
  { key: 'evaluation', label: 'Criteria Evaluation' },
  { key: 'deliberation', label: 'Deliberation' },
  { key: 'awarding', label: 'Final Awarding' },
];

/**
 * Evaluation's Nominees tab — the four STAGE GATES as a vertical rail at the panel's left edge
 * (product call, 03 Nov 2026), the `<ApplicantFilterRail>` shape: the shared `<ProfileNav>` in an aside,
 * each entry with its count — `w-64`, not the twin's `w-56`, which cut "Requirements Qualification"
 * short. Each entry reads the nominees AT that stage:
 *
 * ```
 * Requirements Qualification   every nominee — tag Qualified / Disqualified       award-evaluations.qualify
 * Criteria Evaluation          the qualified — score each criterion               award-evaluations.score
 * Deliberation                 the qualified, ranked — name the winners           award-evaluations.deliberate
 * Final Awarding               the winners — approve, confer, write the 201 file  award-evaluations.award
 * ```
 *
 * STRICT: the server refuses a stage out of order, and each control here is disabled for that STATE
 * with the reason. Deliberation and Final Awarding are decisions about the AWARD, so they carry a card
 * above the table — the decision's facts, its documents, and the cog that records it.
 */
export function NomineeStagesPanel({
  surface,
  award,
  onChanged,
}: {
  surface: PraiseSurface;
  award: AwardDetail;
  /** Anything moved — the profile refetches the award. */
  onChanged: () => void;
}) {
  const [gate, setGate] = useState<AwardStageGate>('qualification');
  const [counts, setCounts] = useState<Partial<Record<AwardStageGate, number>>>({});
  const [deciding, setDeciding] = useState<'deliberation' | 'awarding' | null>(null);
  const decision = gate === 'deliberation' || gate === 'awarding';

  const closed = !award.isActive
    ? 'This award is deleted.'
    : award.awardedAt != null
      ? 'The final awarding is recorded, so every stage is fixed.'
      : null;

  // THE GATE INTO DELIBERATION — the server's own three conditions, in its order, so the two agree.
  const untagged = award.untaggedCount ?? 0;
  const qualified = award.qualifiedCount ?? 0;
  const unscored = qualified - (award.scoredQualifiedCount ?? 0);
  const deliberationBlocked =
    closed ??
    (untagged > 0
      ? `${untagged} ${untagged === 1 ? 'nominee is' : 'nominees are'} not yet tagged at Requirements Qualification.`
      : qualified === 0
        ? 'No nominee is qualified, so there is no one to deliberate on.'
        : unscored > 0
          ? `${unscored} qualified ${unscored === 1 ? 'nominee has' : 'nominees have'} not been scored yet.`
          : null);

  const awardingBlocked = closed ?? (award.deliberatedAt == null ? 'Record the deliberation first — the final awarding approves its winners.' : null);

  const deliberationDocuments = useStageDocumentsRow({
    award,
    stage: AWARD_STAGE.Deliberation,
    description: 'Minutes, a resolution — what the panel decided on.',
    closedReason: closed,
    onChanged,
  });

  const awardingDocuments = useStageDocumentsRow({
    award,
    stage: AWARD_STAGE.FinalAwarding,
    description: 'The signed approval, the citation.',
    closedReason: awardingBlocked,
    onChanged,
  });

  const recordedRows = (at: string | null | undefined, by: string | null | undefined) => [
    {
      label: 'Recorded',
      value:
        at != null ? (
          <time dateTime={toIso(at)} className="font-mono">
            {formatDateTime(at)}
          </time>
        ) : null,
    },
    { label: 'Recorded by', value: by ?? null },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0">
      <aside
        aria-label="Stage gates"
        className="modern-scrollbar w-64 shrink-0 overflow-y-auto overflow-x-hidden border-r border-slate-200 bg-white"
      >
        <ProfileNav
          id={`${surface.key}-award-stages`}
          entries={GATES.map((g) => ({ key: g.key, label: g.label, count: counts[g.key] }))}
          activeKey={gate}
          onSelect={(key) => setGate(GATES.find((g) => g.key === key)?.key ?? 'qualification')}
        />
      </aside>

      {/*
        A DECISION stage carries its card ABOVE the table, so the column scrolls as ONE region and the
        table is sized by its rows; the two nominee stages keep the table filling the panel.
      */}
      <div
        className={
          decision
            ? 'modern-scrollbar flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden'
            : 'flex min-h-0 min-w-0 flex-1 flex-col'
        }
      >
        {gate === 'deliberation' ? (
          <div className="px-4 pt-4">
            <ProfileCard
              id={`${surface.key}-award-deliberation`}
              title="Deliberation"
              onEdit={() => setDeciding('deliberation')}
              editLabel={award.deliberatedAt == null ? 'Record deliberation' : 'Change winners'}
              editPermission="award-evaluations.deliberate"
              editDisabledReason={deliberationBlocked}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  {
                    label: 'Winners',
                    value:
                      award.deliberatedAt != null
                        ? `${counts.awarding ?? 0} of ${award.winnerCount}`
                        : `Up to ${award.winnerCount} — not recorded yet`,
                    mono: award.deliberatedAt != null,
                  },
                  ...recordedRows(award.deliberatedAt, award.deliberatedByName),
                  { label: 'Remarks', value: award.deliberationRemarks ?? null },
                  deliberationDocuments.row,
                ]}
              />
            </ProfileCard>
          </div>
        ) : gate === 'awarding' ? (
          <div className="px-4 pt-4">
            <ProfileCard
              id={`${surface.key}-award-final`}
              title="Final awarding"
              onEdit={() => setDeciding('awarding')}
              editLabel="Record final awarding"
              editPermission="award-evaluations.award"
              editDisabledReason={awardingBlocked}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  {
                    label: 'Awarded on',
                    // A CALENDAR date — never the instant path.
                    value: award.awardedOn != null ? formatDateOnly(award.awardedOn) : null,
                    mono: award.awardedOn != null,
                  },
                  ...recordedRows(award.awardedAt, award.awardedByName),
                  { label: 'Remarks', value: award.awardingRemarks ?? null },
                  awardingDocuments.row,
                ]}
              />
            </ProfileCard>
          </div>
        ) : null}

        {/* Remounted when a decision lands, so the stage's own column re-reads. */}
        <NomineesPanel
          key={`${gate}-${award.deliberatedAt ?? ''}-${award.awardedAt ?? ''}`}
          surface={surface}
          award={award}
          onChanged={onChanged}
          stage={gate}
          onStageCounts={setCounts}
          fill={!decision}
        />
      </div>

      {deliberationDocuments.modals}
      {awardingDocuments.modals}

      {deciding === 'deliberation' ? (
        <DeliberationModal
          award={award}
          onClose={() => setDeciding(null)}
          onSaved={() => {
            setDeciding(null);
            onChanged();
          }}
        />
      ) : null}

      {deciding === 'awarding' ? (
        <FinalAwardingModal
          award={award}
          onClose={() => setDeciding(null)}
          onSaved={() => {
            setDeciding(null);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}
